import {
  Alert,
  AudioPlayerControls,
  AwaitContainer,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  SidebarTrigger,
  Skeleton,
  Spinner,
  useSidebar,
} from '@ibsys/kit';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConversation } from '@/entities/conversations';
import { useHasPermission } from '@/entities/profile';
import { useTranscriptEventFilters } from '@/entities/settings';
import { useDialogSessionOptional } from '@/features/DialogSession';
import { cn } from '@/shared/lib';
import { Tooltip, UnsavedChangesDialog, useMainLayout } from '@/shared/ui';
import { DialogTranscription } from './DialogTranscription';
import { DownloadTranscriptButton } from './DownloadTranscriptButton';
import { TranscriptEditModeButton } from './TranscriptEditor/TranscriptEditModeButton';
import { TranscriptEditor } from './TranscriptEditor/TranscriptEditor';
import { useTranscriptEditMode } from './TranscriptEditor/useTranscriptEditMode';
import { TranscriptEventFilters } from './TranscriptEventFilters';

type Props = {
  dialogId: number;
  showAudio?: boolean;
  isCollapsible?: boolean;
};

export const DialogTranscriptSection: FC<Props> = React.memo((props) => {
  const { dialogId, showAudio = true, isCollapsible = true } = props;
  const { t } = useTranslation(['dialogs', 'common']);

  const { data: conversation, isLoading: isLoadingConversation } = useConversation(dialogId);
  const dialogSession = useDialogSessionOptional();
  const audioStatus = dialogSession?.audioStatus ?? 'ready';

  // Опция «Показать без анонимизации» доступна, только когда у диалога есть неанонимизированная версия.
  const hasNonAnonymizedVersion = conversation?.hasNonAnonymizedVersion ?? false;

  // Состояние тумблера: при наличии DialogSession (страница диалогов) — в сессии,
  // чтобы запрос транскрипта, скролл и аудио-привязки работали с тем же видом;
  // иначе (например, диалог из выборки) — локальное состояние секции.
  const [localShowNonAnonymized, setLocalShowNonAnonymized] = useState(false);
  const showNonAnonymized = dialogSession?.showNonAnonymized ?? localShowNonAnonymized;
  const setShowNonAnonymized = dialogSession?.setShowNonAnonymized ?? setLocalShowNonAnonymized;

  const { open } = useSidebar();
  const { isMobile } = useMainLayout();

  const showDialog = open || !isCollapsible;

  // Переключатель назван действием, доступным сейчас: развёрнутую панель он сворачивает,
  // свёрнутую — разворачивает
  const togglePanelLabel = open ? t('dialogs:collapseDialogPanel') : t('dialogs:expandDialogPanel');

  // Отбор событий читается из настроек пользователя — из того же места, что и на шкале записи,
  // поэтому лента реплик и шкала всегда показывают одни и те же события.
  const { eventFilters, setEventFilters } = useTranscriptEventFilters();

  const canEditTranscript = useHasPermission('EDIT_CONVERSATION_TRANSCRIPT');
  const { editor, isEditMode, isExitConfirmOpen, toggleEditMode, confirmExit, cancelExit } = useTranscriptEditMode();

  const renderAudio = () => {
    if (audioStatus === 'unavailable') {
      return (
        <Alert
          className={cn('text-muted-foreground w-full items-center p-0.5 ', isMobile && 'text-xs')}
          title={t('audioUnavailable')}
        />
      );
    }
    // Запись ещё подгружается — индикатор в зарезервированном месте, чтобы плеер появился без скачка разметки.
    if (audioStatus === 'pending') {
      return (
        <div className={'w-full flex items-center justify-center'}>
          <Spinner className={'size-5'} />
        </div>
      );
    }
    // Состояние записи ещё неизвестно — зарезервированное место без индикатора, чтобы не было
    // дёрганья индикатор → «недоступна» у диалогов без записи.
    if (audioStatus === 'loading') {
      return <Skeleton className={'h-8 w-full'} />;
    }
    return <AudioPlayerControls className={'w-full'} size={'md'} />;
  };

  return (
    <Card className="flex h-full gap-0 p-0">
      <AwaitContainer isLoading={isLoadingConversation} className="overflow-hidden rounded-xl flex flex-col">
        <CardHeader
          className={cn(
            !showDialog && 'px-2',
            'py-0 md:py-2 flex gap-4 items-center justify-between relative h-12 md:pt-3',
          )}
        >
          {!isMobile && (
            <div className="flex gap-4 items-center">
              {isCollapsible && (
                <Tooltip tooltip={togglePanelLabel}>
                  <SidebarTrigger aria-label={togglePanelLabel} />
                </Tooltip>
              )}
              {showDialog && <CardTitle>{t('dialog')}</CardTitle>}
            </div>
          )}
          {isMobile && showAudio && renderAudio()}
          <div className="flex gap-2 items-center ">
            {showDialog && canEditTranscript && (
              <TranscriptEditModeButton
                isActive={isEditMode}
                isUnavailable={showNonAnonymized}
                onToggle={toggleEditMode}
              />
            )}
            {showDialog && (
              <TranscriptEventFilters
                value={eventFilters}
                onChange={setEventFilters}
                label={isMobile ? '' : t('dialogs:filters.title')}
                canShowNonAnonymized={hasNonAnonymizedVersion}
                showNonAnonymized={showNonAnonymized}
                isShowNonAnonymizedDisabled={isEditMode}
                onShowNonAnonymizedChange={setShowNonAnonymized}
              />
            )}
            {showDialog && <DownloadTranscriptButton dialogId={dialogId} />}
          </div>
        </CardHeader>
        {showAudio && !isMobile && (
          <CardHeader className={cn(!open && 'px-2', 'flex gap-4 items-center relative py-0.5! min-h-14')}>
            {renderAudio()}
          </CardHeader>
        )}
        {showDialog && (
          <CardContent className={'overflow-hidden h-full px-0 flex flex-col'}>
            <div className="flex-1 min-h-0">
              {isEditMode ? (
                <TranscriptEditor dialogId={dialogId} editor={editor} />
              ) : (
                <DialogTranscription dialogId={dialogId} eventFilters={eventFilters} anonymized={!showNonAnonymized} />
              )}
            </div>
          </CardContent>
        )}
      </AwaitContainer>
      <UnsavedChangesDialog
        isOpen={isExitConfirmOpen}
        onOpenChange={(open) => !open && cancelExit()}
        onConfirm={confirmExit}
        title={t('common:unsavedChangesTitle')}
        description={t('dialogs:transcriptEdit.exitDescription')}
        confirmLabel={t('dialogs:transcriptEdit.exitWithoutSaving')}
        cancelLabel={t('common:cancel')}
        className="max-w-md"
      />
    </Card>
  );
});
